import { css, html } from 'react-strict-dom';
import { spacing } from 'src/constants/spacing.css';

import { Api } from '../../../api';
import { Text } from '../../../components';
import { Style } from '../../../constants';
import { useMediaQuery } from '../../../hooks';
import {
  DESKTOP_SEGMENT_COUNT,
  MOBILE_SEGMENT_COUNT,
  STATE_VISUALS,
  formatAvailability,
  stateFromStatus,
} from '../status';
import UptimeBar from './UptimeBar';

type Resource = Api.CompanyUptime.V['resources'][number];

type Props = { resource: Resource };

const MOBILE_QUERY = '(max-width: 575px)';

export default function ServiceUptimeCard({ resource }: Props) {
  const visual = STATE_VISUALS[stateFromStatus(resource.status)];
  const days = useMediaQuery(MOBILE_QUERY)
    ? MOBILE_SEGMENT_COUNT
    : DESKTOP_SEGMENT_COUNT;

  return (
    <html.div style={styles.card}>
      <html.div style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
        <html.div style={[Style.row, Style.alignCenter, styles.titleRow]}>
          <html.span
            style={[styles.dot, styles.dotColor(visual.foreground)]}
            aria-hidden
          />
          <html.div>
            <Text typeface="body.medium" color="neutral100">
              {resource.publicName}
            </Text>
            <html.div style={[Style.row, Style.alignCenter, styles.meta]}>
              <Text typeface="caption.medium" color="neutral60">
                {visual.label}
              </Text>
              {resource.resourceType && (
                <html.span style={Style.hideBelowXs}>
                  <Text typeface="caption.medium" color="neutral60">
                    {`· ${resource.resourceType}`}
                  </Text>
                </html.span>
              )}
            </html.div>
          </html.div>
        </html.div>
        <Text
          typeface="body.medium"
          color={resource.availability === null ? 'neutral40' : 'neutral100'}
        >
          {formatAvailability(resource.availability)}
        </Text>
      </html.div>

      <html.div style={styles.barBlock}>
        <Text typeface="caption.medium" color="neutral60">
          {`Last ${days} days`}
        </Text>
        <UptimeBar
          statusHistory={resource.statusHistory}
          days={days}
          label={`${resource.publicName} uptime, last ${days} days`}
        />
      </html.div>
    </html.div>
  );
}

const styles = css.create({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: `calc(${spacing.unit} * 0.5)`,
    paddingBlock: spacing.unit,
  },
  titleRow: {
    gap: `calc(${spacing.unit} * 0.5)`,
  },
  meta: {
    gap: `calc(${spacing.unit} * 0.5)`,
  },
  dot: {
    flexShrink: 0,
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  dotColor: (value: string) => ({
    backgroundColor: value,
  }),
  barBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: `calc(${spacing.unit} * 0.5)`,
  },
});
